import React, { useState } from 'react';
import { Clock3 } from 'lucide-react';
import './gate-demo.css';

// The same gates as on the boards, with the rules the truth tables print.
const GATES = {
  AND: { fn: (a, b) => a && b, hint: 'HIGH only when A and B are both HIGH.' },
  OR: { fn: (a, b) => a || b, hint: 'HIGH when A or B is HIGH.' },
  XOR: { fn: (a, b) => (a ? 1 : 0) !== (b ? 1 : 0), hint: 'HIGH when A and B differ.' },
  NAND: { fn: (a, b) => !(a && b), hint: 'The opposite of AND.' },
  NOR: { fn: (a, b) => !(a || b), hint: 'The opposite of OR.' },
  XNOR: { fn: (a, b) => (a ? 1 : 0) === (b ? 1 : 0), hint: 'HIGH when A and B match.' },
  NOT: { fn: (a) => !a, unary: true, hint: 'Inverts A.' },
  BUFFER: { fn: (a) => a, unary: true, hint: 'Output follows A.' },
};

const BODY = {
  AND: { d: 'M110,25 H145 A35,35 0 0 1 145,95 H110 Z', outX: 180 },
  NAND: { d: 'M110,25 H145 A35,35 0 0 1 145,95 H110 Z', outX: 180, bubble: true },
  OR: { d: 'M110,25 C135,25 158,40 172,60 C158,80 135,95 110,95 C120,72 120,48 110,25 Z', outX: 172 },
  NOR: { d: 'M110,25 C135,25 158,40 172,60 C158,80 135,95 110,95 C120,72 120,48 110,25 Z', outX: 172, bubble: true },
  XOR: { d: 'M120,25 C145,25 166,40 180,60 C166,80 145,95 120,95 C130,72 130,48 120,25 Z', outX: 180, extraArc: true },
  XNOR: { d: 'M120,25 C145,25 166,40 180,60 C166,80 145,95 120,95 C130,72 130,48 120,25 Z', outX: 180, extraArc: true, bubble: true },
  NOT: { d: 'M110,30 L170,60 L110,90 Z', outX: 170, bubble: true },
  BUFFER: { d: 'M110,30 L170,60 L110,90 Z', outX: 170 },
};

const Led = ({ on, cx, cy, r }) => (
  <g>
    {on && <circle cx={cx} cy={cy} r={r + 4} fill="rgba(255,182,39,0.25)" />}
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={on ? 'var(--pcb-led)' : 'var(--pcb-led-deep)'}
      stroke={on ? 'var(--pcb-led)' : 'rgba(255,182,39,0.4)'}
      strokeWidth="1.5"
    />
  </g>
);

const GateSymbol = ({ gate, a, b, out }) => {
  const { d, outX, bubble, extraArc } = BODY[gate];
  const unary = GATES[gate].unary;
  const stroke = 'var(--pcb-silk)';
  const outStart = bubble ? outX + 14 : outX;
  const yA = unary ? 60 : 45;

  return (
    <svg viewBox="0 0 258 120" className="gate-demo-symbol" role="img" aria-label={`${gate} gate: A ${Number(a)}${unary ? '' : `, B ${Number(b)}`}, output Y ${Number(out)}`}>
      {/* input wires + LEDs */}
      <line x1="58" y1={yA} x2="112" y2={yA} stroke={stroke} strokeWidth="2" />
      <Led on={a} cx={45} cy={yA} r={6} />
      {!unary && (
        <g>
          <line x1="58" y1="75" x2="112" y2="75" stroke={stroke} strokeWidth="2" />
          <Led on={b} cx={45} cy={75} r={6} />
        </g>
      )}
      {/* gate body */}
      {extraArc && <path d="M108,25 C118,48 118,72 108,95" fill="none" stroke={stroke} strokeWidth="2" />}
      <path d={d} fill="var(--pcb-cell)" stroke={stroke} strokeWidth="2" />
      {bubble && <circle cx={outX + 7} cy="60" r="7" fill="var(--pcb-cell)" stroke={stroke} strokeWidth="2" />}
      {/* output wire + LED */}
      <line x1={outStart} y1="60" x2="222" y2="60" stroke={stroke} strokeWidth="2" />
      <Led on={out} cx={236} cy={60} r={9} />
    </svg>
  );
};

const InputSwitch = ({ name, value, disabled = false, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={value}
    aria-label={`Input ${name}${disabled ? ' (not used by this gate)' : ''}`}
    disabled={disabled}
    className="pcb-switch gate-demo-input"
    onClick={onChange}
  >
    <span className="pcb-switch-name">{name}</span>
    <span className="pcb-switch-track" aria-hidden="true">
      <span className="pcb-switch-knob" />
    </span>
    <span className="gate-demo-input-value" aria-hidden="true">
      {disabled ? 'Unused' : value ? '1 · HIGH' : '0 · LOW'}
    </span>
  </button>
);

const STATUS = {
  live: 'Live: the diagram and output follow the inputs.',
  sampled: 'Clocked: the diagram and output show the last sampled inputs.',
  pending: 'Inputs changed. Pulse the clock to update the diagram and output.',
};

// Stack all possible messages in one grid cell. Invisible alternatives reserve
// the largest natural height at any width or text size, without clipping text.
const StableMessage = ({ messages, selected, className = '' }) => (
  <div className={`gate-demo-message ${className}`}>
    {Object.entries(messages).map(([key, message]) => (
      <p key={key} className={key === selected ? '' : 'gate-demo-reserved'}
        aria-hidden={key !== selected}>
        {message}
      </p>
    ))}
  </div>
);

const GateDemo = () => {
  const [gate, setGate] = useState('AND');
  const [a, setA] = useState(true);
  const [b, setB] = useState(true);
  const [clocked, setClocked] = useState(false);
  const [sampled, setSampled] = useState({ a: true, b: true });
  const [pulses, setPulses] = useState(0);

  const { fn, unary } = GATES[gate];
  const shownA = clocked ? sampled.a : a;
  const shownB = clocked ? sampled.b : b;
  const out = !!fn(shownA, shownB);
  const pending = clocked && (a !== sampled.a || (!unary && b !== sampled.b));
  const status = clocked ? (pending ? 'pending' : 'sampled') : 'live';
  const rows = unary
    ? [[false], [true]]
    : [[false, false], [false, true], [true, false], [true, true]];
  const isLive = (row) => row[0] === shownA && (unary || row[1] === shownB);

  const changeMode = (manual) => {
    if (manual === clocked) return;
    setClocked(manual);
    setSampled({ a, b });
    setPulses(0);
  };

  const pulseClock = () => {
    setSampled({ a, b });
    setPulses((count) => count + 1);
  };

  return (
    <div className="gate-demo">
      <div className="gate-demo-gates" role="group" aria-label="Choose a gate">
        {Object.keys(GATES).map((g) => (
          <button key={g} type="button"
            className={`pcb-gate-btn ${g === gate ? 'active' : ''}`}
            aria-pressed={g === gate} onClick={() => setGate(g)}>
            {g}
          </button>
        ))}
      </div>

      <div className="gate-demo-modes" role="group" aria-label="Output update mode">
        <button type="button" aria-pressed={!clocked} onClick={() => changeMode(false)}>Live inputs</button>
        <button type="button" aria-pressed={clocked} onClick={() => changeMode(true)}>Manual clock</button>
      </div>

      <div className="gate-demo-stage">
        <div className="gate-demo-inputs" role="group" aria-label="Input switches">
          <InputSwitch name="A" value={a} onChange={() => setA((value) => !value)} />
          <InputSwitch name="B" value={b} disabled={unary} onChange={() => setB((value) => !value)} />
        </div>

        <div className="gate-demo-diagram">
          <GateSymbol gate={gate} a={shownA} b={shownB} out={out} />
        </div>

        <div className="gate-demo-table">
          <p className="gate-demo-table-label">Truth table</p>
          <table className="pcb-truth" aria-label={`${gate} truth table`}>
            <thead><tr><th scope="col">A</th><th scope="col">{unary ? '—' : 'B'}</th><th scope="col">Y</th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.join('')} className={isLive(row) ? 'live' : ''}>
                  <td>{Number(row[0])}</td><td>{unary ? '—' : Number(row[1])}</td><td>{Number(fn(...row))}</td>
                </tr>
              ))}
              {unary && [0, 1].map((row) => (
                <tr className="gate-demo-empty-row" aria-hidden="true" key={`reserved-${row}`}>
                  <td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="gate-demo-clock-row">
        <button type="button" className="gate-demo-clock" disabled={!clocked}
          onClick={pulseClock} aria-describedby="gate-demo-clock-help">
          <Clock3 size={18} aria-hidden="true" /> Pulse clock
        </button>
        <p className="gate-demo-output" role="status" aria-live="polite" aria-atomic="true">
          <span className={`pcb-led-dot ${out ? 'on' : ''}`} aria-hidden="true" />
          <span>Y = {Number(out)}</span>
          <span className="sr-only">{clocked ? `, clock pulse ${pulses}` : ', live output'}</span>
        </p>
      </div>
      <p className="gate-demo-clock-help" id="gate-demo-clock-help">Manual clock samples the inputs when you press Pulse clock.</p>
      <StableMessage messages={STATUS} selected={status} className="gate-demo-status" />
      <StableMessage messages={Object.fromEntries(Object.entries(GATES).map(([name, data]) => [name, data.hint]))}
        selected={gate} className="gate-demo-hint" />
    </div>
  );
};

export default GateDemo;
